import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { lifestyleApi } from '../apiService/api';
import { getAppParams } from '../apiService/timespam';
import GOQiiLoader from '../Components/GOQiiLoader';
import './Theme.css';
import './ChallengeRulePage.css';

const POINTS_ICON = 'https://appcdn.goqii.com/storeimg/18247_1673326658.png';

const Section = ({ title, children }) => (
  <div className="cr-card">
    <h2 className="cr-section-title">{title}</h2>
    {children}
  </div>
);

const ChallengeRulePage = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = getAppParams();
    const challengeId = new URL(window.location.href).searchParams.get('challengeId');

    lifestyleApi
      .fetchChallengeRules({ ...params, challengeId })
      .then((res) => {
        if (res.data?.data) {
          setData(res.data.data);
        } else {
          setError(t('challengeRules.error'));
        }
      })
      .catch(() => setError(t('challengeRules.error')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="cr-page cr-center">
        <GOQiiLoader visible size={80} />
      </div>
    );
  }

  if (error || !data) {
    return <div className="cr-page cr-center cr-error-text">{error || t('challengeRules.error')}</div>;
  }

  return (
    <div className="cr-page">

      {/* ── Challenge header ── */}
      <div className="cr-card cr-header-card">
        {data.img && (
          <img src={data.img} alt={data.challengeTitle} className="cr-challenge-img" />
        )}
        <div className="cr-header-text">
          <h1 className="cr-challenge-title">{data.challengeTitle}</h1>
          {data.challengeSubText && (
            <p className="cr-challenge-sub">{data.challengeSubText}</p>
          )}
        </div>
      </div>

      {/* ── Duration ── */}
      <Section title={t('challengeRules.duration')}>
        <p className="cr-body-text">
          {data.startDate} {t('challengeRules.durationMidnight')} {data.endDate} {t('challengeRules.durationEnd')}
        </p>
      </Section>

      {/* ── Your Mission ── */}
      <Section title={t('challengeRules.mission')}>
        <p className="cr-body-text cr-mb-12">{t('challengeRules.missionDesc')}</p>
        <div className="cr-tasklist">
          {(data.tasklist || []).map((task, idx) => (
            <div key={idx} className="cr-task-row">
              <img src={task.image} alt={task.title} className="cr-task-icon" />
              <div className="cr-task-info">
                <span className="cr-task-title">{task.title}</span>
                {task.subTitle && <span className="cr-task-subtitle">{task.subTitle}</span>}
              </div>
              <div className="cr-task-points">
                <img src={POINTS_ICON} alt="pts" className="cr-points-icon" />
                <span className="cr-points-val">{task.totalPoints}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Tie Breaker ── */}
      {data.tieBreaker && (
        <Section title={t('challengeRules.tieBreaker')}>
          <p className="cr-body-text">{data.tieBreaker}</p>
        </Section>
      )}

      {/* ── Rewards ── */}
      {data.rewardText && (
        <Section title={t('challengeRules.rewards')}>
          {data.uht > 0 && (
            <ul className="cr-reward-list">
              <li>
                {t('challengeRules.uhtWinners', { count: data.uhtWinnersCount, amount: data.uht })}
              </li>
              {data.uhtBadgecount > 0 && (
                <li>{t('challengeRules.uhtBadge', { amount: data.uhtBadgecount })}</li>
              )}
            </ul>
          )}
          {data.goqiiCash > 0 && (
            <p className="cr-body-text">
              {t('challengeRules.goqiiCash', { count: data.goqiiCashWinnersCnt, amount: data.goqiiCash })}
            </p>
          )}
          {data.customRewardsText && (
            <ul className="cr-reward-list">
              {data.customRewardsText.map((val, i) => (
                <li key={i}>{val}</li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {/* ── Rules ── */}
      <Section title={t('challengeRules.rules')}>
        <p className="cr-body-text cr-mb-12">{t('challengeRules.rulesIntro')}</p>
        <div className="cr-rule-list">
          <div className="cr-rule-row">
            <span className="cr-rule-bullet">✓</span>
            <span className="cr-rule-text">{t('challengeRules.rule1')}</span>
          </div>
          <div className="cr-rule-row">
            <span className="cr-rule-bullet">✓</span>
            <span className="cr-rule-text">{t('challengeRules.rule2', { time: data.updateTime })}</span>
          </div>
          <div className="cr-rule-row">
            <span className="cr-rule-bullet">✓</span>
            <span className="cr-rule-text">{t('challengeRules.rule3')}</span>
          </div>
        </div>
      </Section>

      {/* ── FAQs ── */}
      {data.additionalFaqs?.length > 0 && (
        <Section title={t('challengeRules.faqs')}>
          <div className="cr-faq-list">
            {data.additionalFaqs.map((faq) => (
              <div key={faq.id} className="cr-faq-item">
                <h3 className="cr-faq-q">{faq.question}</h3>
                <div
                  className="cr-faq-a"
                  dangerouslySetInnerHTML={{ __html: faq.answer }}
                />
              </div>
            ))}
          </div>
        </Section>
      )}

    </div>
  );
};

export default ChallengeRulePage;
